import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Minus, Plus, Trash2, ShoppingBag, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, subtotal, shipping, total, physicalQty, totalQuantity } =
    useCart();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const startCheckout = async () => {
    const trimmed = (email || '').trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('Please enter a valid email for your receipt', 'Por favor ingresa un email válido para tu recibo'));
      return;
    }
    if (items.length === 0) return;
    setBusy(true);
    try {
      const payload = {
        items: items.map((it) => ({
          album_id: it.album_id,
          variant: it.variant,
          quantity: it.quantity,
        })),
        origin_url: window.location.origin,
        customer_email: trimmed,
      };
      const response = await axios.post(`${API}/checkout/cart`, payload);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Cart checkout failed:', err);
      toast.error(err.response?.data?.detail || t('Failed to start checkout', 'Error al iniciar el pago'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <section className="pt-32 pb-10 bg-charcoal">
        <div className="container mx-auto px-4 md:px-8">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-2">
            {t('Your Cart', 'Tu Carrito')}
          </h1>
          <p className="font-lato text-gray-400">
            {totalQuantity === 0
              ? t('Your cart is empty.', 'Tu carrito está vacío.')
              : t(`${totalQuantity} item${totalQuantity === 1 ? '' : 's'}`, `${totalQuantity} artículo${totalQuantity === 1 ? '' : 's'}`)}
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 md:px-8">
          {items.length === 0 ? (
            <div className="max-w-md mx-auto text-center py-20" data-testid="empty-cart">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-lato text-gray-500 text-lg mb-6">
                {t('Add some music to get started.', 'Añade algo de música para comenzar.')}
              </p>
              <Link to="/music">
                <Button className="bg-gold-500 hover:bg-gold-400 text-white font-lato uppercase tracking-wider">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('Browse Music', 'Ver Música')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Items list */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {items.map((it) => (
                  <div
                    key={`${it.album_id}-${it.variant}`}
                    className="flex gap-4 p-4 bg-cream border border-gold-200 rounded-md"
                    data-testid={`cart-item-${it.album_id}-${it.variant}`}
                  >
                    <img
                      src={it.image_url}
                      alt={it.title}
                      className="w-20 h-20 md:w-24 md:h-24 object-cover rounded shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-playfair text-lg font-bold text-charcoal truncate">
                        {language === 'es' ? (it.title_es || it.title) : it.title}
                      </p>
                      <p className="font-lato text-xs uppercase tracking-wider text-gray-500 mb-2">
                        {it.variant === 'physical'
                          ? t('Physical CD', 'CD Físico')
                          : t('Digital', 'Digital')}
                      </p>
                      <p className="font-lato text-gold-600 font-semibold">${it.price.toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeItem(it.album_id, it.variant)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove"
                        data-testid={`remove-${it.album_id}-${it.variant}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-2 border border-gold-300 rounded">
                        <button
                          onClick={() => updateQuantity(it.album_id, it.variant, it.quantity - 1)}
                          className="px-2 py-1 text-gold-600 hover:bg-gold-50"
                          data-testid={`qty-down-${it.album_id}-${it.variant}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-lato text-sm w-6 text-center" data-testid={`qty-${it.album_id}-${it.variant}`}>
                          {it.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(it.album_id, it.variant, it.quantity + 1)}
                          className="px-2 py-1 text-gold-600 hover:bg-gold-50"
                          data-testid={`qty-up-${it.album_id}-${it.variant}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <aside className="bg-charcoal text-white p-6 rounded-md h-fit lg:sticky lg:top-28" data-testid="cart-summary">
                <h2 className="font-playfair text-2xl font-bold mb-6">
                  {t('Order Summary', 'Resumen del Pedido')}
                </h2>
                <div className="space-y-3 font-lato text-sm pb-4 border-b border-white/10">
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('Subtotal', 'Subtotal')}</span>
                    <span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {t('Shipping', 'Envío')}
                      {physicalQty > 0 && (
                        <span className="text-xs ml-1">
                          ({physicalQty} CD{physicalQty === 1 ? '' : 's'})
                        </span>
                      )}
                    </span>
                    <span data-testid="summary-shipping">
                      {shipping > 0 ? `$${shipping.toFixed(2)}` : t('Free', 'Gratis')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 pt-1">
                    {t(
                      'Sales tax calculated at checkout based on your address.',
                      'Impuesto sobre la venta calculado al pagar según tu dirección.'
                    )}
                  </p>
                </div>
                <div className="flex justify-between items-baseline py-4 border-b border-white/10">
                  <span className="font-playfair text-lg">{t('Total', 'Total')}</span>
                  <span className="font-playfair text-2xl font-bold text-gold-400" data-testid="summary-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
                <div className="mt-6">
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">
                    {t('Email for receipt', 'Email para el recibo')}
                  </label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white text-charcoal mb-4"
                    data-testid="cart-email"
                  />
                  <Button
                    onClick={startCheckout}
                    disabled={busy}
                    className="w-full bg-gold-500 hover:bg-gold-400 text-white font-lato uppercase tracking-wider py-6"
                    data-testid="cart-checkout-button"
                  >
                    {busy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {t('Starting checkout…', 'Iniciando pago…')}
                      </>
                    ) : (
                      <>
                        {t('Continue to Payment', 'Continuar al Pago')}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {t('Secure checkout via Stripe', 'Pago seguro vía Stripe')}
                  </p>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
